import axios from "axios";
import { Anthropic } from "@anthropic-ai/sdk";
import { updateUserTokens, trackLesson, checkLessonLimit } from "./functions";
import OpenAI from "openai";

import Logger from "./Logger";
import {
  collection,
  query,
  where,
  serverTimestamp,
  addDoc,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { db } from "./firebase"; // Adjust this import path to where your firebase config is
const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY, // Make sure to add this to your environment variables
  dangerouslyAllowBrowser: true, // Required to use the SDK in browser environments
});

// const apiKey = 'sk-proj-C69fw7mjS0f4DK64gRHhT3BlbkFJLhrh35xtgTCTy4UOD9yP';

// const openAiKey = process.env.OPENAI_API_KEY;
const API_URL =
  process.env.NODE_ENV === "production"
    ? "/api"
    : process.env.REACT_APP_DEV_API_URL || "http://localhost:3001/api";

export const callOpenAITTS2 = async (
  text,
  voice = "alloy",
  signal,
  userId = null,
) => {
  try {
    const instructions =
      'Voice Affect: Calm, composed, and reassuring; project quiet authority and confidence.\n\nTone: Sincere, empathetic, and gently authoritative—express genuine apology while conveying competence.\n\nPacing: Steady and moderate; unhurried enough to communicate care, yet efficient enough to demonstrate professionalism.\n\nEmotion: Genuine empathy and understanding; speak with warmth, especially during apologies ("I\'m very sorry for any disruption...").\n\nPronunciation: Clear and precise, emphasizing key reassurances ("smoothly," "quickly," "promptly") to reinforce confidence.\n\nPauses: Brief pauses after offering assistance or requesting details, highlighting willingness to listen and support.';

    // Create the speech response
    const response = await openai.audio.speech.create(
      {
        model: "gpt-4o-mini-tts",
        voice: voice,
        input: text,
        instructions,
      },
      { signal },
    );
    Logger.log("RES: ", response);

    // The response is a ReadableStream
    // Convert to blob to maintain compatibility with your existing component
    return await response.blob();
  } catch (error) {
    console.error("Error in TTS generation:", error);
    throw error;
  }
};

// Update your existing callApi function (in your frontend code)
const callApiRag = async (
  messages,
  maxTokens = 6000,
  clean = false,
  userId = null,
  temperature = 0.7,
  useRag = true, // Add this parameter with default true
) => {
  const startTime = performance.now();
  try {
    if (userId) {
      const canGenerate = await checkLessonLimit(userId);
      if (!canGenerate) {
        throw new Error("LESSON_LIMIT_REACHED_IN_CALL_API");
      }
    }

    Logger.log("Sending messages to API:", messages);

    // Use the RAG endpoint instead of the regular OpenAI endpoint
    const endpoint = useRag ? `${API_URL}/openai-rag` : `${API_URL}/openai`;

    const response = await axios.post(endpoint, {
      messages,
      maxTokens,
      temperature,
    });

    await addDoc(collection(db, "api_logs"), {
      request: { messages, maxTokens, temperature, useRag },
      response: response.data,
      userId,
      timestamp: serverTimestamp(),
    });

    Logger.log("API response:", response.data);
    const totalTokens = response.data.usage.total_tokens;

    if (userId) {
      updateUserTokens(userId, totalTokens);
      Logger.log(
        "updateUserTokens ran from callApi on userId:",
        userId,
        " with increased tokens of ",
        totalTokens,
      );
    }

    Logger.log("data.Usage: ", response.data.usage);
    Logger.log("maxTokens defined:", maxTokens);
    Logger.log("totalTokens used:", totalTokens);

    if (!clean) return response.data.choices[0].message.content;
    else return response;
  } catch (error) {
    console.error("Error calling API:", error);
    return null;
  } finally {
    const endTime = performance.now();
    const elapsedTime = (endTime - startTime) / 1000;
    Logger.log(`API call took ${elapsedTime} seconds`);
  }
};

const generateImage = async (
  prompt,
  userId,
  size,
  model,
  numImages,
  quality,
  responseFormat,
) => {
  try {
    const response = await axios.post(`${API_URL}/generate-image`, {
      prompt: prompt,
      n: numImages,
      size: size,
      model: model,
      quality: quality,
      responseFormat: responseFormat,
    });

    // Log the response data to understand its structure
    console.log("Response data:", response.data);
    console.log("Full API response:", JSON.stringify(response.data, null, 2));
    const estimatedTokens = estimateTokensForDallE(prompt);
    updateUserTokens(userId, estimatedTokens);

    //await trackLesson(userId, "ImageGenerator.js", null, null);

    // Access the image URL from the response data
    const imageUrl = response.data.data[0].url;

    // Return the image URL
    return response.data;
  } catch (error) {
    console.error("Error generating image:", error);
    throw error; // Re-throw the error to handle it in the calling function
  }
};

const callOpenAISTT = async (audioBlob, userId) => {
  try {
    const formData = new FormData();
    formData.append("file", audioBlob, "audio.webm");
    formData.append("model", "whisper-1");

    const response = await axios.post(`${API_URL}/stt`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    // Estimate tokens for the transcribed text
    const estimatedTokens = estimateTokens([
      { content: response.data.text, role: "" },
    ]);
    Logger.log(`Estimated tokens for STT text: ${estimatedTokens}`);
    updateUserTokens(userId, estimatedTokens);

    return response.data.text;
  } catch (error) {
    console.error("Error calling STT API:", error);
    throw error;
  }
};

const callOpenAITTS = async (text, voice = "alloy", signal, userId) => {
  const estimatedTokens = estimateTokens([{ content: text, role: "" }]);
  Logger.log(
    `Estimated tokens for TTS text: ${estimatedTokens} for userId `,
    userId,
  );
  updateUserTokens(userId, estimatedTokens);
  try {
    const response = await axios.post(
      `${API_URL}/tts`,
      {
        text,
        voice,
      },
      {
        responseType: "arraybuffer",
        signal: signal,
      },
    );

    return new Blob([response.data], { type: "audio/mpeg" });
  } catch (error) {
    if (axios.isCancel(error)) {
      Logger.log("TTS request was cancelled");
    } else {
      console.error("Error calling TTS API:", error);
    }
    throw error;
  }
};

const callClaude = async (messages, maxTokens, clean = false) => {
  try {
    const response = await axios.post(`${API_URL}/claude`, {
      messages,
      maxTokens,
    });

    Logger.log("callClaude: ", response);
    return response;
    //return response.data.content[0].text;
  } catch (error) {
    console.error("Error calling Claude:", error);
    throw error;
  }
};

const callApi = async (
  messages,
  maxTokens = 6000,
  clean = false,
  userId = null,
  temperature = 0.7,
) => {
  const startTime = performance.now();
  try {
    if (userId) {
      const canGenerate = await checkLessonLimit(userId);
      if (!canGenerate) {
        Logger.log("throws error here for user id ", userId);
        throw new Error("LESSON_LIMIT_REACHED_IN_CALL_API"); // This is all we need - just return null when limit is hit
      }
    }

    Logger.log("Sending messages to API:", messages);
    const response = await axios.post(`${API_URL}/openai`, {
      messages,
      maxTokens,
      temperature,
    });

    await addDoc(collection(db, "api_logs"), {
      request: { messages, maxTokens, temperature },
      response: response.data,
      userId,
      timestamp: serverTimestamp(),
    });
    Logger.log("API response:", response.data);
    const totalTokens = response.data.usage.total_tokens;
    if (userId) {
      updateUserTokens(userId, totalTokens);
      Logger.log(
        "updateUserTokens ran from callApi on userId:",
        userId,
        " with increased tokens of ",
        totalTokens,
      );
    }

    Logger.log("data.Usage: ", response.data.usage);
    Logger.log("maxTokens defined:", maxTokens);
    Logger.log("totalTokens used:", totalTokens);

    if (!clean) return response.data.choices[0].message.content;
    else return response;
  } catch (error) {
    console.error("Error calling API:", error);
    return null;
  } finally {
    const endTime = performance.now();
    const elapsedTime = (endTime - startTime) / 1000;
    Logger.log(`API call took ${elapsedTime} seconds`);
  }
};

const callSearchApi = async (
  messages,
  maxTokens = 8000,
  clean = false,
  userId = null,
  temperature = 0.7,
  userLocation = null,
  searchContextSize = "medium",
) => {
  const startTime = performance.now();
  try {
    if (userId) {
      const canGenerate = await checkLessonLimit(userId);
      if (!canGenerate) {
        Logger.log("throws error here for user id ", userId);
        throw new Error("LESSON_LIMIT_REACHED_IN_CALL_API");
      }
    }

    Logger.log("Sending messages to Search API:", messages);

    const response = await axios.post(`${API_URL}/openai-search-mini`, {
      messages,
      maxTokens,
      temperature,
      userLocation,
      searchContextSize,
    });

    await addDoc(collection(db, "api_logs"), {
      request: { messages, maxTokens, temperature, searchType: "web_search" },
      response: response.data,
      userId,
      timestamp: serverTimestamp(),
    });

    Logger.log("Search API response:", response.data);

    // Note: Search API may have different usage tracking
    const totalTokens = response.data.usage?.total_tokens || 0;
    if (userId && totalTokens > 0) {
      updateUserTokens(userId, totalTokens);
      Logger.log(
        "updateUserTokens ran from callSearchApi on userId:",
        userId,
        " with increased tokens of ",
        totalTokens,
      );
    }

    if (!clean) {
      return response.data.choices[0].message.content;
    } else {
      return response;
    }
  } catch (error) {
    console.error("Error calling Search API:", error);
    return null;
  } finally {
    const endTime = performance.now();
    const elapsedTime = (endTime - startTime) / 1000;
    Logger.log(`Search API call took ${elapsedTime} seconds`);
  }
};

const estimateTokens = (messages) => {
  // Uppskattning: 1 token ≈ 4 tecken för engelska texter
  const CHARS_PER_TOKEN = 4;

  let totalChars = 0;

  for (const message of messages) {
    totalChars += message.content.length;
    // Lägg till extra tecken för rollnamnet (t.ex. "user" eller "assistant")
    totalChars += message.role.length;
  }

  // Avrunda uppåt för att ge en konservativ uppskattning
  return Math.ceil(totalChars / CHARS_PER_TOKEN);
};

const estimateTokensForDallE = (prompt) => {
  // Uppskattning: 1 token ≈ 4 tecken för engelska texter
  const CHARS_PER_TOKEN = 4;

  // Total character count for the prompt
  let totalChars = prompt.length;

  // Avrunda uppåt för att ge en konservativ uppskattning
  return Math.ceil(totalChars / CHARS_PER_TOKEN);
};

export const callApiWithStreaming = async (
  messages,
  onChunk,
  userId = null,
  signal = null,
  namespace = null,
  additionalPromptInstructions = null,
  rag = false,
  teacherId,
) => {
  const initialTokens = estimateTokens(messages);
  let totalTokens = initialTokens;

  try {
    const fetchOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        messages: messages,
        stream: true,
        stream_options: {
          include_usage: true,
        },
        namespace: namespace,
        additionalPromptInstructions: additionalPromptInstructions,
        teacherId: teacherId,
      }),
    };

    if (signal) {
      fetchOptions.signal = signal;
    }

    const response = await fetch(
      `${API_URL}/${rag ? "openai-rag-stream" : "openai-stream"}`,
      fetchOptions,
    );
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (
              parsed.choices &&
              parsed.choices[0].delta &&
              parsed.choices[0].delta.content
            ) {
              onChunk(parsed.choices[0].delta.content);
            }

            if (parsed.usage) {
              totalTokens = parsed.usage.total_tokens;
            }
          } catch (error) {
            console.error("Error parsing streaming message:", error);
          }
        }
      }
    }

    if (userId) {
      updateUserTokens(userId, totalTokens);
      Logger.log("updateUserTokens with totalToken: ", totalTokens);
    }

    return buffer;
  } catch (error) {
    if (error.name === "AbortError") {
      Logger.log("Streaming request was aborted");
    } else {
      console.error("Error in streaming API call:", error);
    }
    throw error;
  }
};

// export const callApiWithStreaming2 = async (messages, onChunk, abortSignal) => {
//   const response = await fetch('https://api.openai.com/v1/chat/completions', {
//     method: 'POST',
//     headers: {
//       'Authorization': `Bearer ${apiKey}`,
//       'Content-Type': 'application/json'
//     },
//     body: JSON.stringify({
//       model: 'gpt-4o-mini',
//       messages: messages,
//       stream: true
//     }),
//     signal: abortSignal
//   });

//   const reader = response.body.getReader();
//   const decoder = new TextDecoder('utf-8');
//   let buffer = '';

//   while (true) {
//     const { done, value } = await reader.read();
//     if (done) break;
//     buffer += decoder.decode(value, { stream: true });
//     const lines = buffer.split('\n');
//     buffer = lines.pop();
//     for (const line of lines) {
//       if (line.startsWith('data: ')) {
//         const data = line.slice(6);
//         if (data === '[DONE]') continue;
//         try {
//           const parsed = JSON.parse(data);
//           const content = parsed.choices[0].delta.content;
//           if (content) {
//             onChunk(content);
//           }
//         } catch (error) {
//           console.error('Error parsing streaming message:', error);
//         }
//       }
//     }
//   }
//   return buffer;
// };

export {
  callApi,
  callClaude,
  callOpenAITTS,
  callOpenAISTT,
  generateImage,
  callApiRag,
  callSearchApi,
};
